<template>
  <v-btn @click="exportTableItems()" color="white" prepend-icon="mdi-download">
    <span>{{ exportMessage }}</span>
    <AlertDialog ref="alertDialog"/>
  </v-btn>
</template>

<script>
import exportExcel from '../helpers/exportExcel';
import { mapGetters } from 'vuex';
import HTTP from '../plugins/backendAPI';
import AlertDialog from './AlertDialog.vue';
import { getFilenameFromContentDisposition } from '../helpers/filenameFromContentDisposition';

export default {
  components: { 
    AlertDialog
  },
  props: ["tableItems", "tableType", "other", "exportMessage", "workspaces", "page", "itemsPerPage", "sortBy", "search", "category"],
  data() {
    return {
      exportArray: []
    }
  },
  computed: {
    ...mapGetters([
      "islegacy",
      "organizationitems",
      "workspaceid",
      "organizationid",
    ]),
  },
  methods: {
    async exportTableItems() {
        if (this.tableType == "inventory" || this.tableType == "all-inventories" || this.tableType == "internal-log") {
          try {
            this.$store.dispatch("setLoading", true);
            let response;
            
            if (this.tableType == "inventory"){
              response = await HTTP.get(`/inventory/exportExcel?workspace-id=${this.workspaceid}&inventory-type-id=${this.other.selectedInventoryType.id}`,
              { responseType: 'blob' });
            } else if (this.tableType == "all-inventories"){
              response = await HTTP.get(`/inventory/exportExcel?workspace-id=${this.workspaceid}`, 
              { responseType: 'blob' });
            } else if (this.tableType == "internal-log") {            
              response = await HTTP.post("/issuelogs/exportExcel", {
                issueId: this.other.selectedIssue.id,
                issueCreatedDate: String(this.other.selectedIssue.created).replace(" ", "T") + ":00",
                qrId: this.other.selectedIssue.qrid,
                organizationId: this.organizationid,
                workspaceId: this.workspaceid,
                showDeleted: this.other.showDeleted,
                showEdits: this.other.showEdits
              },
              {
                responseType: 'blob'
              });
            }

            const contentDisposition = response.headers['content-disposition'];
            const filename = getFilenameFromContentDisposition(contentDisposition);
            
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;            
            link.download = filename;

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          
            console.log('Export successful!:', response.data);
            await this.$refs.alertDialog.showDialog('Export Successful', `File "${filename}" saved locally.`, '$success');

        } catch (error) {
          console.error('Export failed:', error);
          await this.$refs.alertDialog.showDialog('Export Failed', error.response?.data || error.message, '$error');
        } finally {
          this.$store.dispatch("setLoading", false);
        }
        } else {
          try {
            await this.createExportObjectArray()
          } catch (e) {
            console.error(e)
          } finally {
            exportExcel(this.exportArray, this.tableType)
          }
        }
    },
    async createExportObjectArray() {
      this.exportArray = []
      this.exportObject = {}
      this.$store.dispatch("setLoading", true)
      if (this.tableType == "qr_codes") {
        await HTTP.post(`/qr/get?workspace-id=${this.workspaceid}`, {
          page: this.page,
          itemsPerPage: this.itemsPerPage,
          sortBy: this.sortBy,
          search: this.search,
          category: this.category
        }).then((response) => {
          response.data.mappedQrCodes.forEach(qrCode => {
            this.exportObject["QRID"] = this.islegacy ? qrCode.qrString : qrCode.qrWorkspaceId
            this.exportObject["Category"] = qrCode.qrCategoryDescription
            this.exportObject["Description"] = qrCode.description
            this.exportObject["Location"] = qrCode.location
            this.exportObject["Url"] = qrCode.url
            this.exportObject["QR url"] = qrCode.qrUrl

            this.exportArray.push(this.exportObject)
            this.exportObject = {}
          });
        }).catch(() => {
        }).finally(() => {
          this.$store.dispatch("setLoading", false)
        })
      } else if (this.tableType == "users") {
        await HTTP.get(`/users/get?organization-id=${this.organizationid}`).then(response => {
          response.data.forEach(user => {
            this.exportObject["First name"] = user.firstname
            this.exportObject["Last name"] = user.lastname
            this.exportObject["E-mail"] = user.email
            this.exportObject["Organization"] = user.role === 0 ? this.organizationitems.find(o => o.id === user.organizationId).name : this.organizationitems.filter(o => user.ownerOrganizations.some(oo => oo.id === o.id)).map(o => o.name).join("; ")
            this.exportObject["Workspaces"] = user.role === 0 ? this.workspaces.filter(w => user.workspaceIds.some(wId => wId === w.id)).map(w => w.name).join("; ") : user.role === 1 ? this.workspaces.filter(w => user.ownerOrganizations.some(oo => oo.id === w.organizationId)).map(w => w.name).join("; ") : this.workspaces.filter(w => w.organizationId === user.organizationId).map(w => w.name).join("; ")
            this.exportObject["Administrator"] = user.isAdministrator
            this.exportObject["Owner"] = user.role === 1

            this.exportArray.push(this.exportObject)
            this.exportObject = {}
          });
        }).catch(() => {
        }).finally(() => [
          this.$store.dispatch("setLoading", false)
        ]);
      } else if (this.tableType == "issues") {
        await HTTP.post("/issues/get?workspace-id=" + this.workspaceid, {}).then((response) => {
          response.data.mappedIssues.forEach(issue => {
            this.exportObject["Header"] = issue.header
            this.exportObject["Type"] = issue.type
            this.exportObject["Alias type"] = issue.aliasTypeDescription
            this.exportObject["Created at"] = issue.created
            this.exportObject["Status"] = issue.status == 0 ? "waiting" : issue.status == 1 ? "ongoing" : issue.status == 2 ? "closed" : "unknown"
            // eslint-disable-next-line no-undef
            issue.picturename !== null && issue.imageId !== null ? this.exportObject["Image"] = `${process.env.VUE_APP_CLIENTURL}/imageViewer/bilder/${issue.imageId}?imageName=${issue.picturename}` : this.exportObject["Image"] = null
            this.exportObject["QrCode"] = issue.description
            this.exportObject["Inventory"] = issue.inventoryDescription
            this.exportObject["Location"] = issue.location
            this.exportObject["QrCategory"] = issue.qrCategory
            this.exportObject["Responsible username"] = issue.name
            this.exportObject["Responsible email"] = issue.email
            this.exportObject["Issuer email"] = issue.issuerEmail
            this.exportObject["Issuer message"] = issue.message

            this.exportArray.push(this.exportObject)
            this.exportObject = {}
          });
        }).catch(() => {
        }).finally(() => [
          this.$store.dispatch("setLoading", false)
        ]);
      } else {
        this.$store.dispatch("setLoading", false)
        throw new Error("Not a valid table for exporting")
      }
    }
  }
}
</script>
